import { DateTime } from 'luxon';
import {
  CheckForPlanAdjustmentsResult,
  CheckForPlanAdjustmentsSolved,
  PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS,
  PlanUpdateOutcome,
  PlanUpdateParams,
  PlanUpdateUserMessage,
} from '@cf-shared/plan-update-interface';
import { PlanUpdate } from '@cf-shared/plan-update';
import { Log_Entry, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { ImmutableState, DeclaredState, FitnessState } from '@cf-shared/data-model/user-state';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { Log_Workout, isStrengthTestWorkout } from '@cf-shared/data-model/log-workout';
import {
  Log_WeeklyCheckup,
  WeeklyCheckupCompletionAction,
} from '@cf-shared/data-model/log-weekly-checkup';
import {
  CoachClientSlot,
  CoachClientStatus,
  CoachRelationship,
  isSlotSleeping,
} from '@cf-shared/data-model/coach';
import { COACH_LINK_REPAIR_MIN_CLAIM_AGE_MS } from '@cf-shared/data-model/coach-link-repair';

/** Three-bucket status mirroring the home Progress-tab Goal-card indicator. */
export enum ClientDashboardStatus {
  OnTrack = 'OnTrack',
  OffTrack = 'OffTrack',
  Undetermined = 'Undetermined',
}

export type CoachActionItemType = 'checkup' | 'strengthTest';

export interface CoachActionItem {
  type: CoachActionItemType;
  logId: string;
  /** ISO UTC time of the underlying log entry (for sort/display). */
  dateUtc: string;
}

export interface CoachDashboardRow {
  clientUid: string;
  status: ClientDashboardStatus | 'Unknown';
  items: CoachActionItem[];
  /** Whether this client's account shows the coach's handover actually finished (#4817). */
  handover: HandoverState;
}

/**
 * Whether a claimed client's account carries proof that the coach's handover
 * finished (#4817).
 *
 * Three answers rather than a boolean, because "we did not check" and "we
 * checked and it is not there" mean completely different things to a coach, and
 * a boolean would force them together. Only `Missing` is worth showing anyone.
 */
export enum HandoverState {
  /** The client's account carries the marker. Everything the claim writes landed. */
  Confirmed = 'confirmed',
  /**
   * The claim was accepted, but the client's account does not show the handover
   * finishing. This is the shape that caused #4738: the coach sees an active
   * client whose account never received the program the coach set up.
   */
  Missing = 'missing',
  /** Nothing is claimed about this client — see `checkClientHandover` for when. */
  NotChecked = 'not-checked',
}

/**
 * The date from which a missing handover marker is meaningful (#4817).
 *
 * Every client claimed before `handoverCompletedAt` existed has no marker, and
 * a broken handover is indistinguishable from an old one. Warning about all of
 * them would put a red badge beside every existing client on every coach's
 * dashboard on upgrade day, which would teach coaches to ignore the badge before
 * it ever meant anything.
 *
 * So the check only judges claims made at or after this instant. It is set well
 * ahead of the release that introduces the marker ON PURPOSE: the marker is
 * written by the CLIENT's app, and native clients update slowly, so a client
 * claiming on an older build shortly after release would otherwise be reported
 * as broken when nothing is wrong. Erring late costs a few weeks of coverage;
 * erring early costs the badge its meaning.
 */
export const HANDOVER_MARKER_SINCE_UTC = '2026-10-01T00:00:00.000Z';

/**
 * How long after a claim this check stays silent, because the marker legitimately
 * is not there yet (#4817).
 *
 * A claim writes `coach-relationship/as-client` to the CLIENT's own device first
 * and only pushes it to the server after signing in — several times over, while it
 * waits for the new role to propagate. The coach's dashboard reads that account
 * from the SERVER. So for a while after a perfectly healthy claim there is no
 * marker to find, and calling that a broken handover would put a false warning in
 * front of a real coach, at exactly the moment they go to look at their new client.
 *
 * This is the same race, in the same documents, that the server's login-time repair
 * already guards — so the window is that one rather than a second number that can
 * drift away from it. The constant is named for where the race was first handled,
 * not for that repair alone. Being slow to warn costs nothing: an genuinely broken
 * handover is still broken an hour later, and the coach is still told.
 */
export const HANDOVER_SETTLE_MS = COACH_LINK_REPAIR_MIN_CLAIM_AGE_MS;

/**
 * Decide what a coach may be told about one claimed client's handover (#4817).
 *
 * `clientCoachRel` is the client's own `coach-relationship/as-client`
 * document, read out of the client's database, or undefined when it is not
 * there.
 *
 * `couldReadClientAccount` must be false whenever that read did not actually
 * happen — pass `ClientDocsRead.ok`, never anything derived from the documents
 * that came back. An absent coach link is the failure being REPORTED here, so
 * inferring "we could not look" from a missing document would hide exactly the
 * case this exists to catch; and without the flag at all, one network hiccup
 * would flag every client on a coach's roster at once.
 */
export function checkClientHandover(args: {
  coachUid: string;
  slot: CoachClientSlot;
  clientCoachRel: CoachRelationship | undefined;
  couldReadClientAccount: boolean;
  now: DateTime;
}): HandoverState {
  const { coachUid, slot, clientCoachRel, couldReadClientAccount, now } = args;

  // we must know WHO is asking before an answer means anything. An empty coach uid
  // matches nobody, so every client's link would compare unequal and the coach's
  // whole roster would be flagged at once — the all-at-once false alarm this
  // function's other guards exist to prevent.
  if (!coachUid) return HandoverState.NotChecked;
  // only a claimed client can have a handover to judge
  if (slot.status !== CoachClientStatus.Claimed) return HandoverState.NotChecked;
  // a parked client is given nothing and counted nowhere (#4530), and that
  // includes not being reported as broken while the coach is not coaching them
  if (isSlotSleeping(slot)) return HandoverState.NotChecked;
  // if the read never happened, absence proves nothing
  if (!couldReadClientAccount) return HandoverState.NotChecked;
  // if we cannot date the claim we cannot apply the cutoff below, and guessing
  // in the direction of warning would flag the oldest clients of all
  if (!slot.claimedAt_utc) return HandoverState.NotChecked;
  if (slot.claimedAt_utc < HANDOVER_MARKER_SINCE_UTC) return HandoverState.NotChecked;
  // the claim may still be in flight — see HANDOVER_SETTLE_MS. An unparseable stamp
  // is treated as settled rather than silencing this client for ever, matching how
  // claimIsSettled reads the same field.
  const claimedAt = DateTime.fromISO(slot.claimedAt_utc);
  if (claimedAt.isValid && now.toMillis() - claimedAt.toMillis() < HANDOVER_SETTLE_MS) {
    return HandoverState.NotChecked;
  }

  // the client's account does not name this coach at all — either the document
  // is missing, or it names somebody else. Both mean this coach's handover is
  // not what that account is running.
  if (clientCoachRel?.coachUid !== coachUid) return HandoverState.Missing;
  if (!clientCoachRel.handoverCompletedAt) return HandoverState.Missing;
  return HandoverState.Confirmed;
}

/** Default detection window (days) for `detectActionItems`. Documented bound;
 *  items older than this are not surfaced on the dashboard board (the
 *  notification email/push remains the permanent record). Re-used by callers
 *  that need the same value (orchestration + E2E hook). */
export const DEFAULT_ACTION_ITEM_WINDOW_DAYS = 30;

/**
 * The range of a client's log a coach screen reads to find action items and to
 * run the plan check: the last `DEFAULT_ACTION_ITEM_WINDOW_DAYS` days, as UTC ISO
 * strings for `CoachClientDataService.readClientLogRangeAsync`. The client dialog
 * and the client list both read this one range, so their counts agree.
 *
 * The range ends at the end of the coach's local TOMORROW, so a check-up stored late
 * in the evening of a client west of UTC is not missed (#6697). Only check-ups use
 * the extra day; the strength-test item and the plan check stop at `entriesUpToUtc`.
 * Why one day is enough: `design-docs/coach-mode.md`, "Where the read ends".
 */
export function coachClientLogReadRangeUtc(todayLocal: DateTime): { startUtc: string; endUtc: string } {
  return {
    startUtc: todayLocal.minus({ days: DEFAULT_ACTION_ITEM_WINDOW_DAYS }).toUTC().startOf('day').toISO()!,
    endUtc: todayLocal.plus({ days: 1 }).endOf('day').toUTC().toISO()!,
  };
}

/**
 * The latest moment an entry may carry for the strength-test action item and the
 * plan check: the end of the coach's day in UTC. This is where the coach's log read
 * itself used to end before #6697 widened it for check-ups.
 */
function entriesUpToUtc(nowOrTodayLocal: DateTime): DateTime {
  return nowOrTodayLocal.toUTC().endOf('day');
}

/**
 * Map the solver result to a three-bucket dashboard status. Mirrors the
 * home Goal-card mapping exactly — see `design-docs/coach-mode.md` →
 * Coach Dashboard → "Status compute".
 *
 *   On track     = !needsAdjustment && message === AllOk
 *   Undetermined = !needsAdjustment && message !== AllOk
 *   Off track    = needsAdjustment === true
 */
export function deriveClientStatus(result: CheckForPlanAdjustmentsSolved): ClientDashboardStatus {
  if (result.needsAdjustment) return ClientDashboardStatus.OffTrack;
  if (result.message === PlanUpdateUserMessage.AllOk) return ClientDashboardStatus.OnTrack;
  return ClientDashboardStatus.Undetermined;
}

/**
 * Detect coach-actionable items from a client's log entries.
 *
 * In-scope item types (MVP):
 *   - "weekly checkup — review": `Log_WeeklyCheckup` with
 *     `completionAction === AwaitingCoach && !coachResolvedAction`.
 *   - "strength test — review": `Log_Workout` that
 *     `isStrengthTestWorkout(.)` and has no `coachReviewedAtUtc` stamp.
 *
 * `windowDays` is a deliberate cap (default 30) — items older than the
 * window are NOT surfaced on the board. The notification email/push
 * remains the permanent record for older items. This is a documented
 * bound, not a silent truncation; see `design-docs/coach-mode.md` →
 * Coach Dashboard → "Action items".
 */
export function detectActionItems(
  logs: Log_Entry[],
  nowUtc: DateTime,
  windowDays = DEFAULT_ACTION_ITEM_WINDOW_DAYS,
): CoachActionItem[] {
  const cutoff = nowUtc.minus({ days: windowDays });
  const strengthTestLatest = entriesUpToUtc(nowUtc);
  const items: CoachActionItem[] = [];

  for (const log of logs) {
    if (!log?._id || !log.time_utc) continue;
    const t = DateTime.fromISO(log.time_utc, { zone: 'utc' });
    if (!t.isValid || t < cutoff) continue;

    if (log.type === LogRecordType.WeeklyCheckup) {
      const c = log as Log_WeeklyCheckup;
      if (c.completionAction === WeeklyCheckupCompletionAction.AwaitingCoach && !c.coachResolvedAction) {
        items.push({ type: 'checkup', logId: log._id, dateUtc: log.time_utc });
      }
    } else if (log.type === LogRecordType.Workout) {
      const w = log as Log_Workout;
      // a workout dated past the coach's UTC day is a planned one, not one to review
      if (t > strengthTestLatest) continue;
      if (!w.coachReviewedAtUtc && isStrengthTestWorkout(w)) {
        items.push({ type: 'strengthTest', logId: log._id, dateUtc: log.time_utc });
      }
    }
  }

  // sort items newest-first so consumers iterate from most-recent first
  items.sort((a, b) => b.dateUtc.localeCompare(a.dateUtc));
  return items;
}

/**
 * The client documents the dashboard's status compute needs, as they came back
 * from the coach's authenticated read. Each one is `undefined` when the client's
 * account does not have it — a client who never finished setting the app up.
 */
export interface DashboardSolverInputs {
  immutable: ImmutableState | undefined;
  declared: DeclaredState | undefined;
  fitness: FitnessState | undefined;
  plan: FitnessPlan | undefined;
}

/**
 * How `buildDashboardRow` reports a condition it cannot act on.
 *
 * It is passed in rather than imported so this file stays free of Angular and
 * can be unit-tested on its own. The page hands in `LoggingService`-backed
 * functions; a test hands in ones that record what was reported.
 */
export interface DashboardRowReporter {
  /** Something is wrong, but nobody is interrupted over it. */
  reportProblem(name: string, context: Record<string, unknown>): void;
  /** A caught exception from code that was not expected to throw. */
  reportException(err: unknown): void;
}

/**
 * Decide one coach-dashboard row from documents that have already been fetched.
 *
 * `logs` is the 30-day set the action-item scan uses; the narrower set the plan
 * check is given is filtered out of it here, over
 * `PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS` — the same constant
 * `SvPlanHandlerService.checkForPlanAdjustments` gathers by, so the coach and the
 * client's own app judge the same days. See `design-docs/coach-mode.md` → Coach
 * Dashboard → "Status compute".
 *
 * `todayLocal` is the coach's local now, and `handover` is already decided by the
 * caller, because answering it needs the coach's own identity and slot list.
 *
 * `runPlanCheck` is the plan check itself, defaulting to the real solver. It is a
 * parameter so a test can see exactly which entries were selected for it and can
 * make it fail on demand; no production caller passes it.
 */
export function buildDashboardRow(args: {
  clientUid: string;
  solverInputs: DashboardSolverInputs;
  logs: Log_Entry[];
  todayLocal: DateTime;
  handover: HandoverState;
  reporter: DashboardRowReporter;
  runPlanCheck?: (params: PlanUpdateParams) => CheckForPlanAdjustmentsResult;
}): CoachDashboardRow {
  const { clientUid, solverInputs, logs, todayLocal, handover, reporter } = args;
  const runPlanCheck = args.runPlanCheck ?? (params => new PlanUpdate().checkForPlanAdjustments(params));
  const { immutable, declared, fitness, plan } = solverInputs;

  const items = detectActionItems(logs, todayLocal.toUTC(), DEFAULT_ACTION_ITEM_WINDOW_DAYS);

  // if a document is missing the client never finished setup — show as Unknown
  // rather than crash the solver with a partial input set.
  if (!immutable || !declared || !fitness || !plan) {
    return { clientUid, status: 'Unknown', items, handover };
  }

  const cutoffMillis = todayLocal
    .minus({ days: PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS })
    .startOf('day')
    .toUTC()
    .toMillis();
  // the log read reaches one day further for check-ups (#6697); the plan check keeps
  // the upper bound it had before, so it judges the same entries as it always did
  const latestMillis = entriesUpToUtc(todayLocal).toMillis();
  // if time_utc is missing or unparseable surface it via telemetry rather than
  // silently dropping the doc from the solver inputs (a malformed entry
  // could mask a real bug in the writer otherwise). Dedupe across the two
  // filter passes (weighIns + historyLogs) so the same offending doc doesn't
  // generate two Sentry events per row.
  const reportedInvalidIds = new Set<string>();
  const inWindow = (e: Log_Entry) => {
    const t = DateTime.fromISO(e?.time_utc ?? '', { zone: 'utc' });
    if (!t.isValid) {
      const id = e?._id ?? '';
      if (!reportedInvalidIds.has(id)) {
        reportedInvalidIds.add(id);
        reporter.reportProblem('coach-dashboard: invalid time_utc in client log', {
          clientUid,
          id,
          time_utc: e?.time_utc,
        });
      }
      return false;
    }
    return t.toMillis() >= cutoffMillis && t.toMillis() <= latestMillis;
  };
  const weighIns = logs.filter(e => inWindow(e)
    && e.type === LogRecordType.WeighIn
    && e.state === LogRecordState.Done
    && !isNaN((e as Log_WeighIn).weight_kg)) as Log_WeighIn[];
  const historyLogs = logs.filter(e => inWindow(e)
    && (e.type === LogRecordType.Workout || e.type === LogRecordType.Meal));

  try {
    const result = runPlanCheck({
      immutableState: immutable,
      declaredState: declared,
      fitnessState: fitness,
      fitnessPlan: plan,
      schedule: undefined,
      weighIns,
      historyLogs,
      today_local: todayLocal.toISO(),
    });
    // the solver answers a crash with a failed result rather than by throwing, so the catch below
    // never sees one. A coach must not read a client whose check did not run as a client whose
    // check said nothing was wrong. A returned failure and a thrown one mean the same thing here,
    // so they get the same answer.
    if (result.outcome === PlanUpdateOutcome.Failed) {
      reporter.reportException(new Error(`plan check failed for client: ${result.error}`));
      return { clientUid, status: 'Unknown', items, handover };
    }
    return { clientUid, status: deriveClientStatus(result), items, handover };
  } catch (err) {
    // solver shouldn't throw on valid inputs — surface to telemetry
    reporter.reportException(err);
    return { clientUid, status: 'Unknown', items, handover };
  }
}

/**
 * Deep-link page string for use with
 * `CoachManageDeepLinkService.processCoachManageAsync({ clientUid, page })`. The
 * `page` format matches what coach push notifications and notification
 * emails already produce (e.g. `workout:id~log/2026-06-04T10:00:00.000Z-workout`).
 *
 * Called by `CoachClientDetailPage` when the coach taps a pending action item, while the
 * coaching dashboard switch is on (#5456). The dashboard's own inline-expand UI that used to
 * call it was removed (see `design-docs/coach-mode.md` → "Deep-link form").
 */
export function deepLinkPageForItem(item: CoachActionItem): string {
  if (item.type === 'checkup') return `weekly-checkup:id~${item.logId}`;
  return `workout:id~${item.logId}`;
}

/**
 * 4-tier sort comparator for dashboard rows. See PRD R5:
 *   1. With items — by count desc; tie-break off-track → undetermined → on-track.
 *   2. Off-track, no items.
 *   3. Undetermined, no items.
 *   4. On-track, no items.
 *
 * Unknown rows (fetch failure / offline) sort to the very bottom so they
 * don't drown out actionable rows.
 */
export function sortDashboardRows<R extends Pick<CoachDashboardRow, 'status' | 'items'>>(rows: R[]): R[] {
  return [...rows].sort(compareDashboardRows);
}

function tierRank(row: Pick<CoachDashboardRow, 'status' | 'items'>): number {
  if (row.status === 'Unknown') return 5;
  if ((row.items?.length || 0) > 0) return 1;
  if (row.status === ClientDashboardStatus.OffTrack) return 2;
  if (row.status === ClientDashboardStatus.Undetermined) return 3;
  return 4; // OnTrack
}

function statusRankWithinItems(status: ClientDashboardStatus | 'Unknown'): number {
  if (status === ClientDashboardStatus.OffTrack) return 0;
  if (status === ClientDashboardStatus.Undetermined) return 1;
  return 2; // OnTrack or Unknown
}

function compareDashboardRows(a: Pick<CoachDashboardRow, 'status' | 'items'>, b: Pick<CoachDashboardRow, 'status' | 'items'>): number {
  const ta = tierRank(a);
  const tb = tierRank(b);
  if (ta !== tb) return ta - tb;
  if (ta === 1) {
    // tier 1 — sort by item count desc, then status rank
    const cd = (b.items?.length || 0) - (a.items?.length || 0);
    if (cd !== 0) return cd;
    return statusRankWithinItems(a.status) - statusRankWithinItems(b.status);
  }
  return 0;
}
